/**
 * Helpers for the case where the backend refuses a checkout because the target
 * can no longer receive donations (target reached, case closed, waqf/campaign
 * completed). The checkout endpoint answers with HTTP 406 and an `error_code`
 * of `DONATION_CLOSED`; the donor should land on the "already complete" screen
 * rather than a generic failure pop-up.
 */

export const DONATION_CLOSED_CODE = "DONATION_CLOSED";
export const DONATION_CLOSED_STATUS = 406;

/**
 * Messages returned by older backend builds that predate `error_code`.
 * Matched on a distinctive fragment so wording tweaks do not break detection.
 */
const LEGACY_DONATION_CLOSED_FRAGMENTS = [
  "لاكتمال مبلغ التبرعات",
  "غير متاح حالياً",
  "غير منشور",
];

/**
 * True when the failed checkout request means "this target is no longer
 * accepting donations", as opposed to a payment/network/validation failure.
 */
export function isDonationClosedError(error: any): boolean {
  const response = error?.response;

  if (response?.status !== DONATION_CLOSED_STATUS) {
    return false;
  }

  const data = response?.data ?? {};

  if (data.error_code) {
    return data.error_code === DONATION_CLOSED_CODE;
  }

  // Fallback for backends that only send the localized message.
  const messages: string[] = [
    ...(Array.isArray(data.errors) ? data.errors : []),
    data.message,
  ].filter(Boolean);

  return messages.some((message) =>
    LEGACY_DONATION_CLOSED_FRAGMENTS.some((fragment) =>
      String(message).includes(fragment)
    )
  );
}
